'use client'

import { useEffect, useState } from 'react'
import { Logo } from '@/components/ui/Logo'
import { Button } from '@/components/ui/Button'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { siteConfig } from '@/config/site'

// ============================================================================
// HEADER — responsive site header.
// Only renders navigation destinations that actually exist at this stage.
// (siteConfig.primaryNav is intentionally empty until real sections ship.)
// ============================================================================

export function Header(): React.JSX.Element {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const hasNav = siteConfig.primaryNav.length > 0

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-border bg-background/80 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Logo />

        {/* Navigation — only appears once real destinations exist. */}
        {hasNav && (
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {siteConfig.primaryNav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground"
              >
                {item.label}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          <Button href={siteConfig.primaryCta.href} size="sm" className="hidden sm:inline-flex">
            {siteConfig.primaryCta.label}
          </Button>
        </div>
      </div>
    </header>
  )
}
